import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { config } from '../../../components/Init/Init'
import HttpStatus from 'http-status-codes'
import { convertUrlToWebp, convertUrlToJpg, tokenizer } from '../../utils'
import { useDiamondDashGameIconTout } from '../../../hooks/toutHooks/useDiamondDashGameIconTout'
import { useFlashGameIconTout } from '../../../hooks/toutHooks/useFlashGameIconTout'
import { useJackpotGameIconTout } from '../../../hooks/toutHooks/useJackpotGameIconTout'

const ImageExistContext = createContext()
ImageExistContext.displayName = 'ImageExistContext'

export const useImageExist = () => useContext(ImageExistContext)

export const ImageExistProvider = ({ children }) => {
  const [isGameIconVariantsLoading, setIsGameIconVariantsLoading] = useState(true)
  const [gameIconVariants, setGameIconVariants] = useState(null)
  // All invalid image sources
  const [invalidImageSources, setInvalidImageSources] = useState([])
  // Invalid image sources to be partially logged
  const imagesErrorLogData = useRef([])

  const { getShowJackpotIcon } = useJackpotGameIconTout()
  const { getShowDiamondDashGameIcon } = useDiamondDashGameIconTout()
  const { getShowFlashPrizeIcon, getShowFlashDiamondIcon } = useFlashGameIconTout()

  useEffect(() => {
    fetch(config.gameIconVariantsPath, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    })
      .then(response => {
        if (response.status !== HttpStatus.NOT_FOUND) {
          response.json().then(res => {
            setGameIconVariants(res)
          })
        }
      })
      .catch(e => {
        console.log('[Sweeps] Inside game icon variants catch', e)
      })
      .finally(() => {
        setIsGameIconVariantsLoading(false)
      })
  }, [])

  function tokenizeIconUrl(iconUrl, gameId, standard) {
    return `${process.env.PUBLIC_URL}${tokenizer(iconUrl, {
      '{gameId}': gameId,
      ...(standard ? config.defaultGameIconTypes : config.gameIconTypes)
    })}`
  }

  useEffect(() => {
    if (!config.showNonExistentImagesSrc) {
      return
    }
    const intervalId = setInterval(() => {
      if (imagesErrorLogData.current.length === 0) {
        return
      }
      console.warn('[ImageExistProvider] Images failed to load:', imagesErrorLogData.current)

      // reset errors data after show
      imagesErrorLogData.current = []
    }, config.showNonExistentImagesSrcDelayMs)

    return () => clearInterval(intervalId)
  }, [])

  const addUnavailableUrl = url => {
    // remove " 2x" from end of image src
    const src = url.split(' ')[0]

    if (!invalidImageSources.includes(src)) {
      setInvalidImageSources(sources => [...sources, src])
      imagesErrorLogData.current = [...imagesErrorLogData.current, src]
    }
  }

  const isImageNotUnavailable = url => {
    // remove " 2x" from end of image src
    return invalidImageSources.includes(url.split(' ')[0])
  }

  const getUrlIfAvailable = (url, gameId, standard, isWebp) => {
    if (isWebp && window.Modernizr.webp) {
      const webpIconUrl = convertUrlToWebp(tokenizeIconUrl(url, gameId, standard))

      if (!isImageNotUnavailable(webpIconUrl)) {
        return webpIconUrl
      }
    }
    const iconUrl = tokenizeIconUrl(url, gameId, standard)
    if (!isImageNotUnavailable(iconUrl)) {
      return iconUrl
    }
    return null
  }

  const getIsIncludedInGameVariantsFile = (gameId, iconVariant) => {
    // if gameIconVariants is failed to load checking all icon variants
    if (!gameIconVariants) {
      return true
    }

    return gameIconVariants[gameId] && gameIconVariants[gameId][iconVariant]
  }

  const getSrcSet = (game, breakpoint, standard, convertSvgToJpg = false) => {
    if (isGameIconVariantsLoading || !game.asset) {
      return null
    }
    const { url, jackpotUrl, flashPrizeUrl, diamondDashUrl, flashDiamondUrl, template } = game.slot[breakpoint].content
    let isWebp = gameIconVariants ? !!gameIconVariants[game.asset.id]?.webp : true
    let defaultUrl = url
    if (
      (template === config.gameIconDropsAndWinsSvgTemplate || template === config.gameIconSvgTemplate) &&
      !convertSvgToJpg
    ) {
      isWebp = false
    }
    if (convertSvgToJpg) {
      defaultUrl = convertUrlToJpg(url)
    }

    if (
      getShowFlashDiamondIcon(game.asset.id) &&
      getIsIncludedInGameVariantsFile(game.asset.id, config.gameIconVariantTypes.flashDiamonds)
    ) {
      const flashDiamondAvailableUrl = getUrlIfAvailable(flashDiamondUrl, game.asset.id, standard, isWebp)
      if (flashDiamondAvailableUrl) {
        return flashDiamondAvailableUrl
      }
    }

    if (
      getShowFlashPrizeIcon(game.asset.id) &&
      getIsIncludedInGameVariantsFile(game.asset.id, config.gameIconVariantTypes.flashPrize)
    ) {
      const flashPrizeAvailableUrl = getUrlIfAvailable(flashPrizeUrl, game.asset.id, standard, isWebp)
      if (flashPrizeAvailableUrl) {
        return flashPrizeAvailableUrl
      }
    }

    if (
      getShowDiamondDashGameIcon(game.asset.id) &&
      getIsIncludedInGameVariantsFile(game.asset.id, config.gameIconVariantTypes.diamondDash)
    ) {
      const diamondDashAvailableUrl = getUrlIfAvailable(diamondDashUrl, game.asset.id, standard, isWebp)
      if (diamondDashAvailableUrl) {
        return diamondDashAvailableUrl
      }
    }

    if (
      getShowJackpotIcon(game.asset.id) &&
      getIsIncludedInGameVariantsFile(game.asset.id, config.gameIconVariantTypes.jackpot)
    ) {
      const jackpotAvailableUrl = getUrlIfAvailable(jackpotUrl, game.asset.id, standard, isWebp)
      if (jackpotAvailableUrl) {
        return jackpotAvailableUrl
      }
    }

    const defaultAvailableUrl = getUrlIfAvailable(defaultUrl, game.asset.id, standard, isWebp)

    if (defaultAvailableUrl) {
      return defaultAvailableUrl
    }

    return tokenizeIconUrl(defaultUrl, game.asset.id, standard)
  }

  return <ImageExistContext.Provider value={{ getSrcSet, addUnavailableUrl }}>{children}</ImageExistContext.Provider>
}
